import React from "react";
import { Box, Drawer, useMediaQuery, useTheme as useMuiTheme } from "@mui/material";
import { DrawerHeader } from "./DrawerHeader";
import { DrawerLoadingState } from "./DrawerLoadingState";
import { CategoriesMenu } from "./CategoriesMenu";
import { Category } from "../../../../types/Category";
import { LG } from "../../../../styles/liquidGlass";

interface CategoriesDrawerProps {
    isOpen: boolean;
    loading: boolean;
    error: string | null;
    rootCategories: Category[];
    productosOpen: boolean;
    expandedCategories: number[];
    onClose: () => void;
    onNavigate: (path: string) => void;
    onToggleProductos: () => void;
    onToggleCategory: (categoryId: number) => void;
    onCategoryClick: (category: Category) => void;
    onViewAllClick: (category: Category) => void;
    getSubcategories: (categoryId: number) => Category[];
}

export const CategoriesDrawer: React.FC<CategoriesDrawerProps> = ({
    isOpen,
    loading,
    error,
    rootCategories,
    productosOpen,
    expandedCategories,
    onClose,
    onNavigate,
    onToggleProductos,
    onToggleCategory,
    onCategoryClick,
    onViewAllClick,
    getSubcategories,
}) => {
    const muiTheme = useMuiTheme();
    const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down(900));

    return (
        <Drawer
            anchor="left"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                className: "lg-strong",
                style: {
                    boxShadow:
                        "30px 0 80px -20px rgba(60,40,20,0.25), inset 0 1px 0 rgba(255,255,255,0.85)",
                },
            }}
            ModalProps={{
                keepMounted: true,
                BackdropProps: {
                    sx: { backgroundColor: "rgba(20,16,12,0.18)", backdropFilter: "blur(2px)" },
                },
            }}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 2000,
                "& .MuiDrawer-paper": {
                    width: 308,
                    top: "12px",
                    bottom: "12px",
                    left: "12px",
                    height: "auto",
                    boxSizing: "border-box",
                    borderRadius: "28px !important",
                    display: "flex",
                    flexDirection: "column",
                    background: "var(--glass-bg-strong) !important",
                    color: LG.color.text,
                },
            }}
        >
            <Box sx={{ display: "contents" }} role="presentation">
                <DrawerHeader onClose={onClose} />
                <DrawerLoadingState loading={loading} error={error} />
                {!loading && !error && (
                    <CategoriesMenu
                        rootCategories={rootCategories}
                        productosOpen={productosOpen}
                        expandedCategories={expandedCategories}
                        onNavigate={onNavigate}
                        onToggleProductos={onToggleProductos}
                        onToggleCategory={onToggleCategory}
                        onCategoryClick={onCategoryClick}
                        onViewAllClick={onViewAllClick}
                        getSubcategories={getSubcategories}
                        isMobile={isSmallScreen}
                        onClose={isSmallScreen ? onClose : undefined}
                    />
                )}
            </Box>
        </Drawer>
    );
};
